import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/data";
import { vocabByOwner } from "../../graphql/queries";
import {
  updateUserVocabulary,
  createUserVocabulary,
} from "../../graphql/mutations"; // Update mutation instead of delete
import { fetchSets } from "../../redux/slices/wordSetsSlice"; // Import fetchSets to access the sets
import generateSignedURL from "../../utils/generateSignedURL"; // Adjust the path as needed

// Generate an Amplify client to interact with the GraphQL API
const client = generateClient();

// Define an asynchronous thunk action to fetch the user's vocabulary
export const fetchUserVocabulary = createAsyncThunk(
  "userVocabulary/fetchUserVocabulary", // Action type string
  async ({ owner }, { getState, dispatch }) => {
    let allWords = [];
    let nextToken = null;

    try {
      do {
        // Prepare the variables for the GraphQL query
        const variables = {
          owner, // Owner (user) of the vocabulary
          limit: 100, // Limit the number of results per page to 100 items
          nextToken, // Token for pagination (if any)
        };

        // Execute the GraphQL query to fetch the user's vocabulary
        const response = await client.graphql({
          query: vocabByOwner,
          variables,
        });

        // Accumulate the fetched items and update the nextToken
        const fetchedWords = response.data.vocabByOwner?.items || [];
        allWords = [...allWords, ...fetchedWords];
        nextToken = response.data.vocabByOwner?.nextToken || null;
      } while (nextToken); // Continue fetching until there are no more results

      // Fetch sets if not already loaded
      const { sets } = getState().sets;
      if (sets.length === 0) {
        await dispatch(fetchSets());
      }

      const updatedSets = allWords.reduce((acc, word) => {
        if (word.setName && !word.isHidden) {
          const matchingSet = sets.find((set) => set.setName === word.setName);
          if (matchingSet) {
            if (!acc[word.setName]) {
              acc[word.setName] = {
                setName: word.setName,
                words: [],
                id: matchingSet.id,
              };
            }
            acc[word.setName].words.push(word);
          }
        }
        return acc;
      }, {});

      // Filter out hidden words and return the accumulated items, total number of words, and user sets with ids
      const visibleWords = allWords.filter((word) => !word.isHidden);

      return {
        allWords: allWords,
        items: visibleWords,
        totalWords: visibleWords.length, // Total number of visible words
        userSets: Object.values(updatedSets),
      };
    } catch (error) {
      // Handle errors by rejecting with a promise
      return Promise.reject(error.message);
    }
  }
);

// Define an asynchronous thunk action to mark a word as hidden instead of deleting it
export const hideUserVocabularyWord = createAsyncThunk(
  "userVocabulary/hideUserVocabularyWord", // Action type string
  async ({ id }, { getState }) => {
    try {
      // Prepare the variables for the GraphQL mutation to update the word
      const variables = {
        input: { id, isHidden: true }, // Set isHidden to true
      };

      // Execute the GraphQL mutation to update the word
      await client.graphql({
        query: updateUserVocabulary,
        variables,
      });

      // Return the ID of the updated word
      return id;
    } catch (error) {
      // Handle errors by rejecting with a promise
      return Promise.reject(error.message);
    }
  }
);

// Define an asynchronous thunk action to add a word to the user's vocabulary
export const addWordToUserVocabulary = createAsyncThunk(
  "userVocabulary/addWordToUserVocabulary",
  async (
    { word, translation, owner, setName = null },
    { getState, rejectWithValue }
  ) => {
    try {
      // Log the current state of userWords
      const userWordsIncHidden = getState().userVocabulary.userWordsIncHidden;

      // Check if the word already exists in the user's vocabulary
      const existingWord = userWordsIncHidden.find(
        (userWord) => userWord.word.toLowerCase() === word.toLowerCase()
      );

      if (existingWord) {
        if (existingWord.isHidden) {
          const updatedWord = {
            id: existingWord.id, // Use the existing word's ID
            isHidden: false,
            updatedAt: new Date().toISOString(),
          };

          // Perform the update using the `updateUserVocabulary` mutation
          const response = await client.graphql({
            query: updateUserVocabulary,
            variables: { input: updatedWord },
          });

          return response.data.updateUserVocabulary;
        } else {
          return rejectWithValue("Word already exists in your vocabulary");
        }
      }

      const input = {
        owner,
        word,
        translation,
        setName,
        isHidden: false, // Ensure the word is marked as not hidden
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const response = await client.graphql({
        query: createUserVocabulary,
        variables: { input },
      });

      return response.data.createUserVocabulary;
    } catch (error) {
      console.error("Error during word addition:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to generate signed URLs for images
export const generateWordSignedUrls = createAsyncThunk(
  "sets/generatewordSignedUrls",
  async (words) => {
    try {
      const wordsSignedUrls = await Promise.all(
        words.map(async (word) => {
          const response = await generateSignedURL(word.wordImage, 600); // Generate signed URL with a 10-minute expiration
          const signedUrl = response.url.href;
          return { id: word.id, signedUrl };
        })
      );
      return wordsSignedUrls;
    } catch (error) {
      console.error("Failed to generate signed URLs:", error);
      return [];
    }
  }
);

// Create a Redux slice for handling user vocabulary-related state
const userVocabularySlice = createSlice({
  name: "userVocabulary", // Name of the slice
  initialState: {
    userWords: [], // Array to store the user's vocabulary words
    userWordsIncHidden: [], // Array to store the user's vocabulary words
    userSets: [], // Array to store user's sets
    exerciseWords: [], // Array to store words selected for exercises
    falseTranslations: [], // Array to hold false translations
    loading: false, // Flag to indicate if an operation is in progress
    error: null, // Error message, if any
    nextToken: null, // Token for pagination
    searchTerm: "", // Current search term
    totalWords: 0, // Total number of words in the user's vocabulary
    newWords: [], // Array to store words with progress 0 or Null
    learningWords: [], // Array to store words with progress between 1 and 99
    learnedWords: [], // Array to store words with progress 100
    wordsSignedUrls: {}, // New field to store signed URLs
  },
  reducers: {
    // Reducer to set the current search term
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    // Reducer to set the words selected for exercises
    setExerciseWords: (state, action) => {
      state.exerciseWords = action.payload;
    },
    // Reducer to set the false translations
    setFalseTranslations: (state, action) => {
      state.falseTranslations = action.payload;
    },
    // Reducer to clear the exercise words and false translations
    clearExerciseWords: (state) => {
      state.exerciseWords = [];
      state.falseTranslations = []; // Clear false translations as well
    },
    updateWordProgress: (state, action) => {
      const { id, newProgress } = action.payload;
      const word = state.userWords.find((word) => word.id === id);
      if (word) {
        word.progress = newProgress;
      }
      // Update the newWords, learningWords, and learnedWords arrays accordingly
      const visibleWords = state.userWords.filter((word) => !word.isHidden);
      state.newWords = visibleWords.filter(
        (word) => word.progress === 0 || word.progress === null
      );
      state.learningWords = visibleWords.filter(
        (word) => word.progress > 0 && word.progress < 100
      );
      state.learnedWords = visibleWords.filter((word) => word.progress === 100);
      state.totalWords = visibleWords.length;
    },
    // Reducer to reset the 'added' state
    resetAddedState: (state) => {
      state.added = false;
    },
  },
  extraReducers: (builder) => {
    // Handle the different states of the fetchUserVocabulary thunk action
    builder
      .addCase(fetchUserVocabulary.pending, (state) => {
        state.loading = true; // Set loading to true when the action is pending
      })
      .addCase(fetchUserVocabulary.fulfilled, (state, action) => {
        state.loading = false; // Set loading to false when the action is fulfilled

        // Update the state with the combined unique words and total number of words
        state.userWordsIncHidden = action.payload.allWords; //store all words including hidden
        state.userWords = action.payload.items; //store only visible words
        state.totalWords = action.payload.totalWords; // Store the total number of visible words
        state.userSets = action.payload.userSets; // Store the user sets with ids

        // Categorize the visible words based on their progress
        state.newWords = action.payload.items.filter(
          (word) => word.progress === 0 || word.progress === null
        );
        state.learningWords = action.payload.items.filter(
          (word) => word.progress > 0 && word.progress < 100
        );
        state.learnedWords = action.payload.items.filter(
          (word) => word.progress === 100
        );
      })
      .addCase(fetchUserVocabulary.rejected, (state, action) => {
        state.loading = false; // Set loading to false when the action is rejected
        state.error = action.payload; // Capture the error message
      })
      .addCase(hideUserVocabularyWord.fulfilled, (state, action) => {
        const word = state.userWords.find((word) => word.id === action.payload);
        if (word) {
          word.isHidden = true; // Mark the word as hidden

          const visibleWords = state.userWords.filter((word) => !word.isHidden);

          // Update the state arrays by filtering out hidden words
          state.userWords = visibleWords;
          state.userSets = Object.values(
            visibleWords.reduce((acc, word) => {
              if (word.setName) {
                if (!acc[word.setName]) {
                  acc[word.setName] = {
                    setName: word.setName,
                    words: [],
                  };
                }
                acc[word.setName].words.push(word);
              }
              return acc;
            }, {})
          );

          state.totalWords = visibleWords.length; // Update the total number of visible words

          // Categorize the visible words based on their progress
          state.newWords = visibleWords.filter(
            (word) => word.progress === 0 || word.progress === null
          );
          state.learningWords = visibleWords.filter(
            (word) => word.progress > 0 && word.progress < 100
          );
          state.learnedWords = visibleWords.filter(
            (word) => word.progress === 100
          );
        }
      })
      .addCase(hideUserVocabularyWord.rejected, (state, action) => {
        state.error = action.payload; // Capture the error message
        console.error("Failed to hide word:", action.payload);
      })
      .addCase(addWordToUserVocabulary.fulfilled, (state, action) => {
        state.userWords.push(action.payload);

        state.newWords = state.userWords.filter(
          (word) =>
            (word.progress === 0 || word.progress === null) && !word.isHidden
        );
        state.learningWords = state.userWords.filter(
          (word) => word.progress > 0 && word.progress < 100 && !word.isHidden
        );
        state.learnedWords = state.userWords.filter(
          (word) => word.progress === 100 && !word.isHidden
        );

        state.totalWords = state.userWords.length;
      })
      .addCase(addWordToUserVocabulary.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(generateWordSignedUrls.fulfilled, (state, action) => {
        action.payload.forEach(({ id, signedUrl }) => {
          state.wordsSignedUrls[id] = signedUrl; // Store the signed URL in the state
        });
      });
  },
});

export const {
  resetAddedState,
  setSearchTerm,
  setExerciseWords,
  setFalseTranslations,
  clearExerciseWords,
  updateWordProgress,
} = userVocabularySlice.actions;

export default userVocabularySlice.reducer;
